import { Component, computed, EventEmitter, OnInit, Output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { IBaseItem } from '../../common-items/BaseItem/base-item-form.component';
import {
  iHasCaptionLeft,
  IHasCaptionLeft,
  iHasInputColorControls,
  IHasInputColorControls,
  InteractiveItemComponent
} from '../../common-items/InteractiveItem/interactive-item-form.component';
import {
  DEFAULT_INTERACTIVE_PROPERTIES,
  InteractiveItemProperties
} from '../../common-items/InteractiveItem/interactive-item-properties';

import { extractXmlProps, parseXML, upsertChildren, xmlElementToString } from '../../../../utils/xmlParser';
import {
  extractColorProperties,
  getColors,
  getInputStyle,
  getTextColorsPropertiesStyle
} from '../../../../utils/colors';
import { getOptimalFirstColumnWidth } from '../../../../utils/getOptimalWidth';

interface TextInputProperties {
  text: string;
  style: string;
  stringInput: string;
  inputCaptionLeft: string;
  inputCaptionRight: string;
  maxTextLength: number;
  captionWidth: string;
}

@Component({
  selector: 'lfe-input-item-form',
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './input-item-form.component.html',
  standalone: true,
  styleUrl: './input-item-form.component.css'
})
export class InputItemFormComponent
  extends iHasInputColorControls(iHasCaptionLeft(InteractiveItemComponent<TextInputProperties>))
  implements OnInit, IBaseItem<TextInputProperties>, IHasCaptionLeft, IHasInputColorControls
{
  @Output() updateFirstColumnWidthRequired = new EventEmitter<{ id: string; width: number }>();
  label = '';
  id = '';
  formControl: FormControl<string | null> | undefined;
  properties: InteractiveItemProperties & TextInputProperties = {
    ...DEFAULT_INTERACTIVE_PROPERTIES,
    captionWidth: '',
    inputCaptionLeft: '',
    inputCaptionRight: '',
    maxTextLength: 0,
    stringInput: '',
    style: '',
    text: ''
  };
  initialValue = '';

  firstColumnWidth = computed(() => getOptimalFirstColumnWidth(this.properties.inputCaptionLeft));

  currentId = computed(() => this.properties.customId || this.properties.id);
  htmlId = computed(() => `text_input_${this.formControlId}`);

  currentTextColor = signal('');

  textStyle = computed(() => getTextColorsPropertiesStyle(this.properties.textBgColor, this.currentTextColor()));
  inputStyle = computed(() => getInputStyle(this.currentInputBgColor(), this.currentInputTextColor()));

  getCaptionLeftStyle(): { [key: string]: string } | null {
    if (!this.properties.inputCaptionLeft) {
      return null;
    }

    return {
      width: `${this.firstColumnWidth()}px`,
      overflow: 'hidden',
      'text-overflow': 'ellipsis',
      'white-space': 'nowrap',
      display: 'inline-block',
      ...this.textStyle()
    };
  }

  getCaptionLeftTitle(): string | null {
    if (!this.properties.inputCaptionLeft) {
      return null;
    }
    if (this.firstColumnWidth() <= 0) {
      return null;
    }
    return this.properties.inputCaptionLeft;
  }

  override ngOnInit() {
    super.ngOnInit();

    this.formControlId = this.properties.customId || this.properties.id;

    this.initialValue = this.properties.stringInput;

    this.formControl = new FormControl(this.initialValue);
    this.form().addControl(this.formControlId, this.formControl);

    if (this.properties.maxTextLength) {
      this.formControl?.addValidators([Validators.maxLength(this.properties.maxTextLength)]);
    }

    this.updateFirstColumnWidthRequired.emit({
      id: this.firstSiblingItemId(),
      width: this.firstColumnWidth()
    });
  }

  extractLocalProperties(xml: ChildNode) {
    const currentXmlString = xmlElementToString(xml);
    const currentXml = parseXML(currentXmlString || '');

    const root: Element = currentXml.documentElement;

    const props = extractXmlProps(root);

    const customId = props['CustomID'];
    const id = props['ID'];

    const text = props['Text'] || '';
    const inputCaptionLeft = props['InputCaptionLeft'] || '';
    const inputCaptionRight = props['InputCaptionRight'] || '';
    const captionWidth = props['CaptionWidth'] || '';
    const stringInput = props['StringInput'] || '';
    const style = props['Style'] || '';
    const maxTextLength = Number(props['MaxTextLength'] || '');
    const activationRule = props['ActivationRule'] || '';
    const isGenericRepeatItem = props['IsGenericRepeatItem'] === 'True';

    const { textBgColor, textColor } = getColors(extractColorProperties(props));

    this.currentTextColor.set(textColor);

    const inputColor = this.initInputColorProperties(props);

    const resultValue = props['ResultValue'] || '';
    const resultUser = props['ResultUser'] || '';
    const resultTime = props['ResultTime'] || '';

    return {
      ...DEFAULT_INTERACTIVE_PROPERTIES,
      id,
      customId,
      activationRule,
      text,
      textBgColor,
      inputColor,
      style,
      stringInput,
      inputCaptionLeft,
      inputCaptionRight,
      maxTextLength,
      captionWidth,
      resultValue,
      resultUser,
      resultTime,
      isGenericRepeatItem
    } as InteractiveItemProperties & TextInputProperties;
  }

  onInputChange(event: Event) {
    const target = event.target as HTMLInputElement;
    const value = target.value;

    this.properties.stringInput = value;
    this.properties.resultValue = this.properties.stringInput;
    this.properties.resultTime = new Date().toISOString();
    this.properties.resultUser = this.user().displayName;
  }

  override getXML(): string {
    const element = this.getXmlCloneElement();

    this.handleInputColorStorage(element);

    if (this.properties.resultValue !== this.initialValue) {
      upsertChildren(element, {
        StringInput: this.properties.stringInput
      });

      this.setResultChildren(element);
    }
    return xmlElementToString(element) || '';
  }
}
